import React, {
    useCallback,
    useContext,
    useState,
} from 'react';

import _ from 'lodash';
import { marked } from 'marked';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Container } from '../../components/Container';
import {
    ApiContext,
    CoreContext,
} from '../../providers';
import { media } from '../../services/media';
import { parseHTML } from '../../services/parseHTML';
import { themeStyles } from '../../services/themeStyles';
import { setError } from '../../state';
import {
    Storyblok,
    StoryblokSection,
} from '../../storyblok';
import { isEmptyObject } from '../../utils';

export const AuthenticationMethods = ({
    sectionData,
}: StoryblokSection<Storyblok.Component.AuthenticationMethods | any>) => {
    const { store } = useContext(CoreContext);
    const { Upscore } = useContext(ApiContext);
    const [selectedAuthMethod, setSelectedAuthMethod] = useState<
        'bankid' | 'bankidmobil' | null
    >(null);

    // const redirectUrl = `${window.location.origin}/${sectionData.redirectPath}`;

    const handleAuthMethodSelection = useCallback((authMethod) => {
        setSelectedAuthMethod(authMethod);

        const redirectUrl = `${window.location.origin}/panel/verification`;

        Upscore.getSourceDebtregistryAuthurl({
            authMethod,
            redirectUrl,
        })
            .then((response: any) => {
                const { authUrl } = response;

                window.location.href = authUrl;
            })
            .catch(() => store.dispatch(setError('general')));
    }, []);

    if (!sectionData) return null;

    const {
        authenticationMethods,
        headingImage,
        headingText,
        subHeading,
        description,
        loaderText,
    } = sectionData;

    return (
        <Section>
            <Container sizeGroup="size-1">
                <Wrapper className={`${selectedAuthMethod && 'selected'}`}>
                    <Header>
                        {!isEmptyObject(headingImage) && (
                            <HeaderImage
                                src={headingImage.filename}
                                alt={headingImage.alt}
                            />
                        )}

                        {isEmptyObject(headingImage) && headingText && (
                            <Heading>{headingText}</Heading>
                        )}

                        {subHeading && (
                            <SubHeading>
                                {parseHTML(subHeading, 'markdown')}
                            </SubHeading>
                        )}
                    </Header>

                    <div>
                        {authenticationMethods &&
                            authenticationMethods.map((method: any) => {
                                const { logo, title, description, authMethod } =
                                    method;

                                if (
                                    selectedAuthMethod &&
                                    selectedAuthMethod === authMethod
                                ) {
                                    return (
                                        <LoaderText key={authMethod}>
                                            {loaderText}
                                        </LoaderText>
                                    );
                                }
                                return (
                                    <Method
                                        onClick={() =>
                                            handleAuthMethodSelection(
                                                authMethod,
                                            )
                                        }
                                        type="button"
                                        key={authMethod}
                                        {...(selectedAuthMethod
                                            ? {
                                                  disabled: true,
                                                  pointerEvents: 'none',
                                              }
                                            : {})}
                                    >
                                        <MethodLogo
                                            src={logo.filename}
                                            alt={title}
                                        />
                                        <div style={{ textAlign: 'left' }}>
                                            <MethodTitle>{title}</MethodTitle>
                                            <MethodDescription>
                                                {description}
                                            </MethodDescription>
                                        </div>
                                    </Method>
                                );
                            })}
                    </div>
                </Wrapper>
                {description && (
                    <Footer>{parseHTML(marked(description))}</Footer>
                )}
            </Container>
        </Section>
    );
};

const SectionPengeStyrke = themeStyles(
    'pengestyrke',
    css`
        ${media.min('md')} {
            margin-top: 100px;
        }
    `,
);

const Section = styled.section`
    padding: 40px 15px;

    ${SectionPengeStyrke}
`;

const Wrapper = styled.div`
    background-color: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.2);
    box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    max-width: 500px;
    padding: 20px 0 0 0;

    margin: 10px auto;

    ${media.min('md')} {
        margin: 0 auto 20px;
        padding: 40px 0 0 0;
    }
`;

const Footer = styled.div`
    max-width: 500px;
    margin: 0 auto 20px;
    font-size: 15px;
    line-height: 1.35;

    ${media.min('md')} {
        margin: 0 auto 40px;
    }

    p {
        font-size: inherit;
    }
`;

const MethodPenge = themeStyles(
    'pengestyrke',
    css`
        &:not([disabled]):hover {
            color: #ffffff;
            text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.1);
            background-image: linear-gradient(to top, #293440 0%, #293440 110%);
            cursor: pointer;
        }
    `,
);

const MethodUpscore = themeStyles(
    'upscore',
    css`
        &:not([disabled]):hover {
            color: #ffffff;
            text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.1);
            background-image: linear-gradient(to top, #15d8e2 0%, #54ef93 110%);
        }
    `,
);

const Method = styled.button`
    display: flex;
    text-decoration: none;
    color: #606060;
    font-size: 14px;
    background-color: transparent;
    margin: 0;
    width: 100%;
    cursor: pointer;

    border-color: rgba(0, 0, 0, 0.2);
    border-style: solid;
    border-width: 1px 0 0 0;
    padding: 10px;

    ${media.min('md')} {
        padding: 20px;
    }

    ${MethodPenge}
    ${MethodUpscore}

    &[disabled] {
        opacity: 0.35;
    }
`;

const MethodTitle = styled.h3`
    color: inherit;
    display: block;
    font-size: 16px;
    font-weight: 700;
    margin: 0 0 10px;
`;

const MethodDescription = styled.p`
    color: inherit;
    font-size: 14px;
    margin: 0;
    display: block;
    font-weight: 300;
`;

const MethodLogo = styled.img`
    height: auto;
    width: 30%;
    object-fit: cover;
    margin: 0 10px 0 0;

    ${media.min('md')} {
        width: 100%;
        max-width: 120px;
        height: 70px;
        margin: 0 20px 0 0;
    }
`;

const Header = styled.header`
    padding: 0 20px;
`;

const Heading = styled.h3`
    padding: 0 20px;
    margin: 0 0 20px;

    font-size: 20px;

    ${media.min('md')} {
        font-size: 36px;
        margin: 0 0 40px;
    }
`;

export const SubHeading = styled.div`
    font-size: 16px;

    p {
        font-size: inherit;
        line-height: 1.35;
        margin: 0 0 1em;
    }
`;

const HeaderImage = styled.img`
    max-width: 100%;
    margin: 0 0 20px;
`;

const PengeLoaderText = themeStyles(
    'pengestyrke',
    css`
        background-image: linear-gradient(to top, #293440 0%, #293440 110%);
    `,
);

const UpscoreLoaderText = themeStyles(
    'upscore',
    css`
        background-image: linear-gradient(to top, #15d8e2 0%, #54ef93 110%);
    `,
);

const LoaderText = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    color: #fff;
    font-family: 'Gotham Rounded', sans-serif;
    text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.1);
    text-align: center;
    font-size: 15px;

    ${PengeLoaderText}
    ${UpscoreLoaderText}
`;
